import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react'
import { toast, ToastContainer } from 'react-toastify';
import { BASE_URL } from '../../../config/axios';
import { CSSForSelectModern, DeleteButton, handleErrorToast, ProgressModal, uploadFileInChunks } from '../../CustomFunctions';
import { AwardIcon, Cloud, LucideAward, LucideMessageCircleCode } from 'lucide-react';
import { Modal } from 'react-bootstrap';
import Select from 'react-select';

import './prolongation.css';
import { Attachment, DeleteDustbin, UpdatePencil } from '../../AllSvg';
import { axiosInstance2 } from '../../../config/axiosInstance';
const AddProlongation = ({ project, getData }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);
    const [commentTypeName, setCommentTypeName] = useState(null);
    const [formData, setFormData] = useState({
        project: project,
        description_point: [{ description: '' }],
        remark: '',
        amount: '',
        comment_type: null,
    });
    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});
    const [documentErrors, setDocumentErrors] = useState([]);
    const [descriptionErrors, setDescriptionErrors] = useState([]);
    const fileInputRefs = useRef([]);

    const commentTypeOptions = [
        { name: 'Prolongation', label: 'Prolongation' },
        { name: 'Deviation', label: 'Deviation' },
    ];

    const handleClose = () => {
        setShow(false);
        setFormData({
            project: project,
            description_point: [{ description: '' }],
            remark: '',
            amount: '',
            comment_type: null,
        });
        setDocumentFormData([{
            document_name: '',
            document_file: null,
            prolongationdeviation: null,
        }]);
        setErrors({});
        setDocumentErrors([]);
        setDescriptionErrors([]);
        setCommentTypeName(null);
        setInputState({});
        setDocNames([]);
    };

    // All documents now live in this state instead of formData.documents
    const [documentFormData, setDocumentFormData] = useState([{
        document_name: '',
        document_file: null,
        prolongationdeviation: null,
    }]);

    const handleShow = () => setShow(true);

    const validateForm = () => {
        const newErrors = {};
        const newDocErrors = [];
        const newDescErrors = [];
        let isValid = true;

        // Validate comment type
        if (!commentTypeName) {
            newErrors.commentTypeName = 'Comment type is required!';
            isValid = false;
        }

        // Validate description points - at least one description is required
        const hasValidDescription = formData.description_point.some(
            point => point.description && point.description.trim() !== ''
        );

        if (!hasValidDescription) {
            newDescErrors.push({
                index: 0,
                message: 'At least one description point is required!'
            });
            isValid = false;
        }

        // Validate each description point
        formData.description_point.forEach((point, index) => {
            if (!point.description || point.description.trim() === '') {
                newDescErrors.push({
                    index: index,
                    message: `Description point ${index + 1} is required!`
                });
                isValid = false;
            }
        });

        // Validate documents from documentFormData - if any field is filled, both name and file are required
        documentFormData.forEach((doc, index) => {
            const hasName = doc.document_name && doc.document_name.trim() !== '';
            const hasFile = doc.document_file !== null;

            // If name is filled but file is missing
            if (hasName && !hasFile) {
                newDocErrors.push({
                    index: index,
                    field: 'document_file',
                    message: `File is required for document "${doc.document_name}"`
                });
                isValid = false;
            }

            // If file is attached but name is missing
            if (hasFile && !hasName) {
                newDocErrors.push({
                    index: index,
                    field: 'document_name',
                    message: `Document name is required for file "${doc.document_file.name}"`
                });
                isValid = false;
            }

            // If both name and file are provided, but file is empty object (shouldn't happen but just in case)
            if (hasName && doc.document_file === null) {
                newDocErrors.push({
                    index: index,
                    field: 'document_file',
                    message: `File is required for document "${doc.document_name}"`
                });
                isValid = false;
            }
        });

        setErrors(newErrors);
        setDocumentErrors(newDocErrors);
        setDescriptionErrors(newDescErrors);

        if (!isValid) {
            toast.warning('Please fill in all required fields');
        }

        return isValid;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "green" : "",
        });

        setFormData({ ...formData, [name]: value });

        if (errors[name]) {
            setErrors((prevErrors) => {
                const { [name]: removedError, ...restErrors } = prevErrors;
                return restErrors;
            });
        }
    };

    // Document handlers
    const [docNames, setDocNames] = useState([]);

    // Document handlers with clear functionality
    const handleDocumentChange = (index, field, value) => {
        // If the field is 'document_file', handle file validation and renaming
        if (field === 'document_file') {
            const file = value; // value is the File object from input

            if (file) {
                // Validate file type
                const allowedTypes = [
                    "application/pdf",
                    "application/zip",
                    "application/x-zip-compressed",
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    "application/vnd.ms-excel",
                    "image/png",
                    "image/gif"
                ];

                if (!allowedTypes.includes(file.type) && !file.name.match(/\.(zip|pdf|xls|xlsx|png|gif)$/i)) {
                    toast.error("Only ZIP, PDF, PNG, GIF and Excel files are allowed!");
                    // Clear the file input
                    if (fileInputRefs.current[index]) {
                        fileInputRefs.current[index].value = "";
                    }
                    return;
                }

                // Generate custom filename with timestamp and random code
                const symbols = "!@#$%^&*()_-+=";
                const lowercaseLetters = "abcdefghijklmnopqrstuvwxyz";
                const uppercaseLetters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
                const numbers = "0123456789";
                const now = new Date();
                const date = now.toLocaleDateString("en-GB").split("/").reverse().join("-");
                const time = now.toLocaleTimeString("en-GB", { hour12: false }).replace(/:/g, "-");
                const allChars = symbols + lowercaseLetters + uppercaseLetters + numbers;

                let randomCode = "";
                for (let i = 0; i < 8; i++) {
                    const randomIndex = Math.floor(Math.random() * allChars.length);
                    randomCode += allChars[randomIndex];
                }

                const aimantraFileName = `Aimantra ${date} ${time}_${randomCode} ${file.name}`;
                const customFile = new File([file], aimantraFileName, { type: file.type });

                // Update documentFormData with the new file
                setDocumentFormData((prevDocs) => {
                    const newDocuments = [...prevDocs];
                    newDocuments[index] = {
                        ...newDocuments[index],
                        document_file: customFile,
                        original_filename: aimantraFileName,
                    };
                    return newDocuments;
                });

                // Update docNames
                setDocNames((prev) => {
                    const newNames = [...prev];
                    newNames[index] = aimantraFileName;
                    return newNames;
                });

                // Clear any errors related to this document
                setDocumentErrors(prev => prev.filter(err => err.index !== index));
                setErrors((prevErrors) => {
                    const newErrors = { ...prevErrors };
                    delete newErrors[`${index}-document_file`];
                    delete newErrors[`${index}-original_filename`];
                    return newErrors;
                });

            } else {
                // Clear the file if no file selected
                setDocNames((prev) => {
                    const newNames = [...prev];
                    newNames[index] = "";
                    return newNames;
                });

                setDocumentFormData((prevDocs) => {
                    const newDocuments = [...prevDocs];
                    newDocuments[index] = {
                        ...newDocuments[index],
                        document_file: null,
                        original_filename: "",
                    };
                    return newDocuments;
                });
            }
        } else {
            // For other fields (like document_name), handle normally
            setDocumentFormData((prevDocs) => {
                const updatedDocs = [...prevDocs];
                updatedDocs[index] = { ...updatedDocs[index], [field]: value };
                return updatedDocs;
            });

            // Clear specific document error when user makes changes
            setDocumentErrors(prev => prev.filter(err => err.index !== index || err.field !== field));
        }
    };

    const handleClearDocument = (index) => {
        // Update documentFormData to clear the document_file
        setDocumentFormData((prevDocs) => {
            const newDocuments = [...prevDocs];
            newDocuments[index] = {
                ...newDocuments[index],
                document_file: null,
            };
            return newDocuments;
        });

        // Clear the file input element
        if (fileInputRefs.current[index]) {
            fileInputRefs.current[index].value = "";
        }

        // Clear any errors related to this document
        setDocumentErrors(prev => prev.filter(err => err.index !== index));

        // Clear general errors
        setErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors[`document_${index}_file`];
            delete newErrors[`document_${index}_name`];
            return newErrors;
        });
    };

    const handleAddDocument = () => {
        setDocumentFormData([
            ...documentFormData,
            { document_name: '', document_file: null, prolongationdeviation: null },
        ]);
    };

    const handleRemoveDocument = (index) => {
        if (documentFormData.length <= 1) {
            // If it's the last document, just clear the file
            handleClearDocument(index);
            return;
        }

        const updatedDocs = documentFormData.filter((_, i) => i !== index);
        setDocumentFormData(updatedDocs);
        fileInputRefs.current = fileInputRefs.current.filter((_, i) => i !== index);
        // Clear document errors for removed index
        setDocumentErrors(prev => prev.filter(err => err.index !== index));
    };

    // Get document error helper
    const getDocumentError = (index, field) => {
        return documentErrors.find(err => err.index === index && err.field === field);
    };

    // Description handlers
    const handleDescriptionChange = (index, value) => {
        const updatedPoints = [...formData.description_point];
        updatedPoints[index] = { ...updatedPoints[index], description: value };
        setFormData({ ...formData, description_point: updatedPoints });

        // Clear description error for this index
        setDescriptionErrors(prev => prev.filter(err => err.index !== index));
    };

    const handleAddDescriptionPoint = () => {
        setFormData({
            ...formData,
            description_point: [...formData.description_point, { description: '' }],
        });
    };

    const handleRemoveDescriptionPoint = (index) => {
        if (formData.description_point.length <= 1) {
            // Don't remove the last one, just clear it
            const updatedPoints = [...formData.description_point];
            updatedPoints[index] = { description: '' };
            setFormData({ ...formData, description_point: updatedPoints });
            return;
        }
        setFormData({
            ...formData,
            description_point: formData.description_point.filter((_, i) => i !== index),
        });
        // Clear description errors for removed index
        setDescriptionErrors(prev => prev.filter(err => err.index !== index));
    };

    // Filter out empty descriptions before submission (documents handled separately now)
    const filterEmptyFields = (data) => {
        const filteredDescriptions = data.description_point.filter(point =>
            point.description && point.description.trim() !== ''
        );

        return {
            ...data,
            description_point: filteredDescriptions
        };
    };

    // Filter out documents that don't have both name and file
    const filterEmptyDocuments = (docs) => {
        return docs.filter(doc =>
            doc.document_name && doc.document_name.trim() !== '' &&
            doc.document_file !== null
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Uploading: please wait...");

        // Pre-calculate steps so progress is a single continuous 0->100 across the whole flow
        const validDocs = filterEmptyDocuments(documentFormData);
        const totalSteps = 1 + validDocs.length + (validDocs.length > 0 ? 1 : 0); // create + files + bulk submit
        const stepWeight = 100 / totalSteps;
        let completedSteps = 0;

        const updateOverallProgress = (localPercent) => {
            const overall = completedSteps * stepWeight + (localPercent / 100) * stepWeight;
            setUploadProgress(Math.min(100, Math.round(overall)));
        };

        try {
            const filteredData = filterEmptyFields(formData);

            const payload = new FormData();
            payload.append('project', filteredData.project);
            payload.append('remark', filteredData.remark || '');
            payload.append('amount', filteredData.amount || '');
            payload.append('comment_type', commentTypeName || '');
            payload.append('description_point', JSON.stringify(filteredData.description_point));

            const res = await axiosInstance2.post(
                `/siteproject/prolongationdeviation/`,
                payload,
                {
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                        updateOverallProgress(percentCompleted);
                    },
                }
            );

            if (res.status === 200 || res.status === 201) {
                completedSteps += 1; // main record created
                updateOverallProgress(0);

                const getId = res.data.id;
                const formDataToSend = new FormData();

                for (let [index, doc] of validDocs.entries()) {
                    formDataToSend.append(`documents[${index}].document_name`, doc.document_name);

                    let fileIndex = 0;

                    if (doc.document_file) {
                        const fileId = await uploadFileInChunks(
                            doc.document_file,
                            doc.document_name,
                            (localPercent) => updateOverallProgress(localPercent)
                        );

                        if (!fileId) {
                            console.error(`❌ Failed to upload file: ${doc.document_name}`);
                            completedSteps += 1;
                            updateOverallProgress(0);
                            continue;
                        }

                        formDataToSend.append("file_ids", fileId);
                        formDataToSend.append(`documents[${fileIndex}][document_name]`, doc.document_name);
                        formDataToSend.append(`documents[${fileIndex}][original_filename]`, doc.document_file.name);

                        fileIndex++;
                        completedSteps += 1; // this file's chunked upload done
                        updateOverallProgress(0);
                    }

                    formDataToSend.append(`documents[${index}].prolongationdeviation`, getId);

                    if (doc.document_file) {
                        formDataToSend.append(`documents[${index}].document_file`, doc.document_file);
                    }
                }

                try {
                    if (formDataToSend.has("file_ids")) {
                        const response = await axiosInstance2.post(
                            `/siteproject/documentbulk/`,
                            formDataToSend,
                            {
                                onUploadProgress: (progressEvent) => {
                                    const percentCompleted = Math.round(
                                        (progressEvent.loaded * 100) / progressEvent.total
                                    );
                                    updateOverallProgress(percentCompleted);
                                },
                                headers: { "Content-Type": "multipart/form-data" },
                            }
                        );

                        if (response.status === 200) {
                            setUploadProgress(100);
                            await getData();
                            handleClose();
                            setTimeout(() => {
                                toast.dismiss(loadingToastId);
                                toast.success("Additional Comment updated successfully.", {
                                    autoClose: 1000,
                                    pauseOnHover: true,
                                    closeOnClick: true,
                                });
                            }, 1000);
                        }
                    } else {
                        setUploadProgress(100);
                        await getData();
                        handleClose();
                        setTimeout(() => {
                            toast.dismiss(loadingToastId);
                            toast.success("Additional Comment Added successfully.", {
                                autoClose: 1000,
                                pauseOnHover: true,
                                closeOnClick: true,
                            });
                        }, 1000);
                    }
                } catch (error) {
                    // handleErrorToast(error);
                } finally {
                    setLoading(false);
                    toast.dismiss(loadingToastId);
                }
            } else {
                throw new Error("Unexpected response status: " + res.status);
            }
        } catch (err) {
            setUploadProgress(0);
            toast.dismiss(loadingToastId);
            const errorMessage = err.response?.data?.message || err.message || 'Something went wrong';
            toast.error(`Failed to add comment: ${errorMessage}`, {
                autoClose: 3000,
            });
            console.error('Error:', err);
        } finally {
            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);
            setLoading(false);
        }
    };


    // Helper function to get description error
    const getDescriptionError = (index) => {
        return descriptionErrors.find(err => err.index === index);
    };


    console.log("Form Data:", formData);
    console.log("Document Form Data:", documentFormData);

    return (
        <>
            <button
                title="Add Additional Comment"
                className="btn-animated"
                onClick={handleShow}
            >
                <div className="btn-content">
                    <LucideMessageCircleCode size={18} />
                    <span>Add Additional Comment</span>
                </div>
                <div className="btn-ripple"></div>
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Add" />
            ) : (
                show === true && (
                    <Modal
                        show={show}
                        onHide={handleClose}
                        dialogClassName="modern-project-modal"
                        size="xl"
                    >
                        <Modal.Header className="modern-modal-header">
                            <div className="header-content">
                                <div >
                                    <LucideMessageCircleCode size={25} />

                                </div>
                                <div>
                                    <Modal.Title>Add Additional Comment</Modal.Title>
                                    <p className="header-subtitle">Fill in the details to add a prolongation or deviation comment for the project</p>
                                </div>
                            </div>
                            <button className="close-btn" onClick={handleClose}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </Modal.Header>

                        <Modal.Body className="modern-modal-body">
                            <ToastContainer
                                position="top-center"
                                autoClose={3000}
                                hideProgressBar
                            />
                            <form onSubmit={handleSubmit} className="project-form">
                                {/* Comment Type Section */}
                                <div className="section-title">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                        <path d="M8 10h.01" />
                                        <path d="M12 10h.01" />
                                        <path d="M16 10h.01" />
                                    </svg>
                                    <h4>Comment Information</h4>
                                </div>

                                <div className="form-grid">
                                    <div className="form-field">
                                        <label>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                            Comment Type <span className="required">*</span>
                                        </label>
                                        <Select
                                            options={commentTypeOptions}
                                            onChange={(option) => setCommentTypeName(option?.name || null)}
                                            value={commentTypeOptions.find(opt => opt.name === commentTypeName)}
                                            placeholder="Select comment type..."
                                            styles={CSSForSelectModern}
                                            isSearchable
                                            isClearable
                                        />
                                        {errors.commentTypeName && (
                                            <div className="error-message">{errors.commentTypeName}</div>
                                        )}
                                    </div>

                                    <div className="form-field">
                                        <label>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="10" />
                                                <path d="M12 6v6l4 2" />
                                            </svg>
                                            Amount
                                        </label>
                                        <input
                                            type="number"
                                            name="amount"
                                            placeholder="Enter amount if applicable"
                                            value={formData.amount || ''}
                                            onChange={handleInputChange}
                                            className={inputState.amount ? "success" : ""}
                                        />
                                    </div>

                                    <div className="form-field">
                                        <label>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M12 20h9" />
                                                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                                            </svg>
                                            Remark
                                        </label>
                                        <textarea
                                            name="remark"
                                            placeholder="Any additional remarks..."
                                            value={formData.remark || ''}
                                            onChange={handleInputChange}
                                            rows={2}
                                            className={inputState.remark ? "success" : ""}
                                        />
                                    </div>
                                </div>

                                {/* Description Points Section - Moved before Documents as it's required */}
                                <div className="section-title" style={{ marginTop: "24px" }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M4 4h16v16H4z" />
                                        <line x1="8" y1="8" x2="16" y2="8" />
                                        <line x1="8" y1="12" x2="16" y2="12" />
                                        <line x1="8" y1="16" x2="12" y2="16" />
                                    </svg>
                                    <h4>Description Points</h4>
                                    <span style={{ fontSize: "12px", color: "#ef4444", marginLeft: "auto", fontWeight: "normal" }}>
                                        <span className="required">*</span> At least one required
                                    </span>
                                </div>

                                {formData.description_point.map((point, index) => {
                                    const error = getDescriptionError(index);
                                    const hasError = error || (index === 0 && descriptionErrors.some(e => e.index === 0 && e.message.includes('required')));

                                    return (
                                        <div key={`desc-${index}`} className="form-grid" style={{
                                            borderBottom: "1px solid #f1f5f9",
                                            paddingBottom: "16px",
                                            marginBottom: "16px",
                                            borderLeft: hasError ? "3px solid #ef4444" : "none",
                                            paddingLeft: hasError ? "12px" : "0"
                                        }}>
                                            <div className="form-field" style={{ gridColumn: "span 2" }}>
                                                <label>
                                                    Point {index + 1}
                                                    <span className="required">*</span>
                                                </label>
                                                <textarea
                                                    value={point.description || ''}
                                                    onChange={(e) => handleDescriptionChange(index, e.target.value)}
                                                    placeholder="Enter description point..."
                                                    rows={2}
                                                    className={error ? "error" : (point.description?.trim() ? "success" : "")}
                                                />
                                                {error && (
                                                    <div className="error-message">{error.message}</div>
                                                )}
                                                {index === 0 && !error && descriptionErrors.some(e => e.index === 0 && e.message.includes('required')) && (
                                                    <div className="error-message">Description is required!</div>
                                                )}
                                            </div>
                                            <div></div>
                                            <div className="form-field" style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }}>
                                                <button
                                                    type="button"
                                                    className="btn-secondaryy"
                                                    onClick={() => handleRemoveDescriptionPoint(index)}
                                                    style={{ padding: "8px 16px" }}
                                                >
                                                    {formData.description_point.length <= 1 ? 'Clear' : 'Remove'}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}

                                <button
                                    type="button"
                                    className="btn-secondaryy"
                                    onClick={handleAddDescriptionPoint}
                                    style={{ marginTop: "8px" }}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="12" y1="5" x2="12" y2="19" />
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                    Add Description Point
                                </button>

                                {/* Documents Section */}
                                <div className="section-title" style={{ marginTop: "24px" }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                        <line x1="16" y1="13" x2="8" y2="13" />
                                        <line x1="16" y1="17" x2="8" y2="17" />
                                        <polyline points="10 9 9 9 8 9" />
                                    </svg>
                                    <h4>Documents</h4>
                                    <span style={{ fontSize: "12px", color: "#64748b", marginLeft: "auto", fontWeight: "normal" }}>Optional</span>
                                </div>
                                {documentFormData.map((doc, index) => {
                                    const nameError = getDocumentError(index, 'document_name');
                                    const fileError = getDocumentError(index, 'document_file');
                                    const hasError = nameError || fileError;

                                    return (
                                        <div key={`doc-${index}`} className="form-grid" style={{
                                            borderBottom: "1px solid #f1f5f9",
                                            paddingBottom: "16px",
                                            marginBottom: "16px",
                                            borderLeft: hasError ? "3px solid #ef4444" : "none",
                                            paddingLeft: hasError ? "12px" : "0"
                                        }}>
                                            <div className="form-field">
                                                <label>Document Name</label>
                                                <input
                                                    type="text"
                                                    value={doc.document_name || ''}
                                                    onChange={(e) => handleDocumentChange(index, 'document_name', e.target.value)}
                                                    placeholder="e.g., Approval Letter"
                                                    className={nameError ? "error" : (doc.document_name?.trim() ? "success" : "")}
                                                />
                                                {nameError && (
                                                    <div className="error-message">{nameError.message}</div>
                                                )}
                                            </div>

                                            <div className="form-field">
                                                <label>Upload File</label>
                                                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                                    {!doc.document_file && (
                                                        <div style={{ flex: 1 }}>
                                                            <label htmlFor={`document_file_${index}`} className="svg-icon" style={{ cursor: "pointer", height: "35px" }}>
                                                                <div style={{ height: "20px" }} className={`svg-field form-input align-center ${fileError ? "error" : (doc.document_file ? "success" : "")}`}>
                                                                    <Attachment />

                                                                </div>
                                                            </label>
                                                            <input
                                                                type="file"
                                                                id={`document_file_${index}`}
                                                                accept=".zip, .pdf, .xls, .xlsx, .gif"
                                                                style={{ display: "none" }}
                                                                onChange={(e) => handleDocumentChange(index, 'document_file', e.target.files[0])}
                                                                ref={el => fileInputRefs.current[index] = el}
                                                            />
                                                        </div>
                                                    )}
                                                    {doc.document_file && (
                                                        <button type='button' className="explorer-btn">
                                                            <div className="icon-wrapper">
                                                                <div className="folder-part folder-base"></div>
                                                                <div className="folder-part folder-tab"></div>
                                                                <div className="folder-part folder-accent"></div>
                                                                <div className="folder-part folder-highlight"></div>
                                                            </div>
                                                            <div className="status-indicator"></div>
                                                            <span className="tooltip-label">File Explorer</span>
                                                        </button>
                                                    )}

                                                    {doc.document_file && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleClearDocument(index)}
                                                            className="btn-secondaryy"
                                                            style={{
                                                                padding: "4px 12px",
                                                                fontSize: "12px",
                                                                whiteSpace: "nowrap",
                                                                height: "38px"
                                                            }}
                                                            title="Clear selected file"
                                                        >
                                                            ✕ Clear
                                                        </button>
                                                    )}
                                                </div>

                                                {doc.document_file && (
                                                    <span style={{ fontSize: "12px", color: "#10b981", marginTop: "4px", display: "block" }}>
                                                        ✓ File selected: {doc.document_file.name}
                                                    </span>
                                                )}

                                                {fileError && (
                                                    <div className="error-message">{fileError.message}</div>
                                                )}

                                                {!doc.document_file && doc.document_name?.trim() && !fileError && (
                                                    <span style={{ fontSize: "12px", color: "#f59e0b", marginTop: "4px", display: "block" }}>
                                                        ⚠ Please select a file
                                                    </span>
                                                )}
                                            </div>
                                            <div></div>
                                            {documentFormData.length > 1 &&
                                                <div className="form-field" style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }}>
                                                    <button
                                                        type="button"
                                                        className="btn-secondaryy"
                                                        onClick={() => handleRemoveDocument(index)}
                                                        style={{ padding: "8px 16px" }}
                                                    >
                                                        {documentFormData.length <= 1 ? 'Clear All' : 'Remove'}
                                                    </button>
                                                </div>
                                            }
                                        </div>
                                    );
                                })}

                                <button
                                    type="button"
                                    className="btn-secondaryy"
                                    onClick={handleAddDocument}
                                    style={{ marginTop: "8px" }}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="12" y1="5" x2="12" y2="19" />
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                    Add Another Document
                                </button>

                                {/* Form Actions */}
                                <div className="form-actions">
                                    <button type="button" className="btn-secondaryy" onClick={handleClose}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn-primaryy" disabled={loading}>
                                        {loading ? (
                                            <>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="spinning">
                                                    <circle cx="12" cy="12" r="10" />
                                                    <path d="M12 6v2M12 12v2" />
                                                    <path d="m16 8-2 2M16 16l-2-2" />
                                                    <path d="M8 16l2-2M8 8l2 2" />
                                                </svg>
                                                <span>Submitting...</span>
                                            </>
                                        ) : (
                                            <>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M20 14.66V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5.34" />
                                                    <polygon points="18 2 22 6 12 16 8 16 8 12 18 2" />
                                                </svg>
                                                <span>Add Comment</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                            <ToastContainer
                                position="top-center"
                                autoClose={1000}
                                hideProgressBar={false}
                                newestOnTop={true}
                                closeOnClick
                                rtl={false}
                                pauseOnFocusLoss
                                draggable
                                pauseOnHover
                            />
                        </Modal.Body>
                    </Modal>
                )
            )}
        </>
    );
};


const UpdateProlongation = ({ project, getData, i }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);
    const [commentTypeName, setCommentTypeName] = useState(null);
    const [formData, setFormData] = useState({
        project: project,
        description_point: [{ description: '' }],
        remark: '',
        amount: '',
        comment_type: null,
    });
    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});
    const [documentErrors, setDocumentErrors] = useState([]);
    const [descriptionErrors, setDescriptionErrors] = useState([]);
    const fileInputRefs = useRef([]);

    const commentTypeOptions = [
        { name: 'Prolongation', label: 'Prolongation' },
        { name: 'Deviation', label: 'Deviation' },
    ];

    const handleClose = () => {
        setShow(false);
        setFormData({
            project: project,
            description_point: [{ description: '' }],
            remark: '',
            amount: '',
            comment_type: null,
        });
        setDocumentFormData([{
            document_name: '',
            document_file: null,
            prolongationdeviation: null,
        }]);
        setErrors({});
        setDocumentErrors([]);
        setDescriptionErrors([]);
        setCommentTypeName(null);
        // setUploadProgress(0);
        setInputState({});
        setDocNames([]);
    };

    // All documents now live in this state instead of formData.documents
    const [documentFormData, setDocumentFormData] = useState([{
        document_name: '',
        document_file: null,
        prolongationdeviation: null,
    }]);
    const handleShow = () => {
        setShow(true);

        setFormData({
            ...i,
            description_point: i?.description_point || [{ description: '' }],
        });

        setCommentTypeName(i?.comment_type || null);
    };

    console.log(i, "i");

    const validateForm = () => {
        const newErrors = {};
        const newDocErrors = [];
        const newDescErrors = [];
        let isValid = true;

        // Validate comment type
        if (!commentTypeName) {
            newErrors.commentTypeName = 'Comment type is required!';
            isValid = false;
        }

        // Validate description points - at least one description is required
        const hasValidDescription = formData.description_point.some(
            point => point.description && point.description.trim() !== ''
        );

        if (!hasValidDescription) {
            newDescErrors.push({
                index: 0,
                message: 'At least one description point is required!'
            });
            isValid = false;
        }

        // Validate each description point
        formData.description_point.forEach((point, index) => {
            if (!point.description || point.description.trim() === '') {
                newDescErrors.push({
                    index: index,
                    message: `Description point ${index + 1} is required!`
                });
                isValid = false;
            }
        });

        // Validate documents from documentFormData - if any field is filled, both name and file are required
        documentFormData.forEach((doc, index) => {
            const hasName = doc.document_name && doc.document_name.trim() !== '';
            const hasFile = doc.document_file !== null;

            // If name is filled but file is missing
            if (hasName && !hasFile) {
                newDocErrors.push({
                    index: index,
                    field: 'document_file',
                    message: `File is required for document "${doc.document_name}"`
                });
                isValid = false;
            }

            // If file is attached but name is missing
            if (hasFile && !hasName) {
                newDocErrors.push({
                    index: index,
                    field: 'document_name',
                    message: `Document name is required for file "${doc.document_file.name}"`
                });
                isValid = false;
            }

            // If both name and file are provided, but file is empty object (shouldn't happen but just in case)
            if (hasName && doc.document_file === null) {
                newDocErrors.push({
                    index: index,
                    field: 'document_file',
                    message: `File is required for document "${doc.document_name}"`
                });
                isValid = false;
            }
        });

        setErrors(newErrors);
        setDocumentErrors(newDocErrors);
        setDescriptionErrors(newDescErrors);

        if (!isValid) {
            toast.warning('Please fill in all required fields');
        }

        return isValid;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "green" : "",
        });

        setFormData({ ...formData, [name]: value });

        if (errors[name]) {
            setErrors((prevErrors) => {
                const { [name]: removedError, ...restErrors } = prevErrors;
                return restErrors;
            });
        }
    };

    // Document handlers
    const [docNames, setDocNames] = useState([]);

    // Document handlers with clear functionality
    const handleDocumentChange = (index, field, value) => {
        // If the field is 'document_file', handle file validation and renaming
        if (field === 'document_file') {
            const file = value; // value is the File object from input

            if (file) {
                // Validate file type
                const allowedTypes = [
                    "application/pdf",
                    "application/zip",
                    "application/x-zip-compressed",
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    "application/vnd.ms-excel",
                    "image/png",
                    "image/gif"
                ];

                if (!allowedTypes.includes(file.type) && !file.name.match(/\.(zip|pdf|xls|xlsx|png|gif)$/i)) {
                    toast.error("Only ZIP, PDF, PNG, GIF and Excel files are allowed!");
                    // Clear the file input
                    if (fileInputRefs.current[index]) {
                        fileInputRefs.current[index].value = "";
                    }
                    return;
                }

                // Generate custom filename with timestamp and random code
                const symbols = "!@#$%^&*()_-+=";
                const lowercaseLetters = "abcdefghijklmnopqrstuvwxyz";
                const uppercaseLetters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
                const numbers = "0123456789";
                const now = new Date();
                const date = now.toLocaleDateString("en-GB").split("/").reverse().join("-");
                const time = now.toLocaleTimeString("en-GB", { hour12: false }).replace(/:/g, "-");
                const allChars = symbols + lowercaseLetters + uppercaseLetters + numbers;

                let randomCode = "";
                for (let i = 0; i < 8; i++) {
                    const randomIndex = Math.floor(Math.random() * allChars.length);
                    randomCode += allChars[randomIndex];
                }

                const aimantraFileName = `Aimantra ${date} ${time}_${randomCode} ${file.name}`;
                const customFile = new File([file], aimantraFileName, { type: file.type });

                // Update documentFormData with the new file
                setDocumentFormData((prevDocs) => {
                    const newDocuments = [...prevDocs];
                    newDocuments[index] = {
                        ...newDocuments[index],
                        document_file: customFile,
                        original_filename: aimantraFileName,
                    };
                    return newDocuments;
                });

                // Update docNames
                setDocNames((prev) => {
                    const newNames = [...prev];
                    newNames[index] = aimantraFileName;
                    return newNames;
                });

                // Clear any errors related to this document
                setDocumentErrors(prev => prev.filter(err => err.index !== index));
                setErrors((prevErrors) => {
                    const newErrors = { ...prevErrors };
                    delete newErrors[`${index}-document_file`];
                    delete newErrors[`${index}-original_filename`];
                    return newErrors;
                });

            } else {
                // Clear the file if no file selected
                setDocNames((prev) => {
                    const newNames = [...prev];
                    newNames[index] = "";
                    return newNames;
                });

                setDocumentFormData((prevDocs) => {
                    const newDocuments = [...prevDocs];
                    newDocuments[index] = {
                        ...newDocuments[index],
                        document_file: null,
                        original_filename: "",
                    };
                    return newDocuments;
                });
            }
        } else {
            // For other fields (like document_name), handle normally
            setDocumentFormData((prevDocs) => {
                const updatedDocs = [...prevDocs];
                updatedDocs[index] = { ...updatedDocs[index], [field]: value };
                return updatedDocs;
            });

            // Clear specific document error when user makes changes
            setDocumentErrors(prev => prev.filter(err => err.index !== index || err.field !== field));
        }
    };

    const handleClearDocument = (index) => {
        // Update documentFormData to clear the document_file
        setDocumentFormData((prevDocs) => {
            const newDocuments = [...prevDocs];
            newDocuments[index] = {
                ...newDocuments[index],
                document_file: null,
            };
            return newDocuments;
        });

        // Clear the file input element
        if (fileInputRefs.current[index]) {
            fileInputRefs.current[index].value = "";
        }

        // Clear any errors related to this document
        setDocumentErrors(prev => prev.filter(err => err.index !== index));

        // Clear general errors
        setErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            delete newErrors[`document_${index}_file`];
            delete newErrors[`document_${index}_name`];
            return newErrors;
        });
    };

    const handleAddDocument = () => {
        setDocumentFormData([
            ...documentFormData,
            { document_name: '', document_file: null, prolongationdeviation: null },
        ]);
    };

    const handleRemoveDocument = (index) => {
        if (documentFormData.length <= 1) {
            // If it's the last document, just clear the file
            handleClearDocument(index);
            return;
        }

        const updatedDocs = documentFormData.filter((_, i) => i !== index);
        setDocumentFormData(updatedDocs);
        fileInputRefs.current = fileInputRefs.current.filter((_, i) => i !== index);
        // Clear document errors for removed index
        setDocumentErrors(prev => prev.filter(err => err.index !== index));
    };

    // Get document error helper
    const getDocumentError = (index, field) => {
        return documentErrors.find(err => err.index === index && err.field === field);
    };

    // Description handlers
    const handleDescriptionChange = (index, value) => {
        const updatedPoints = [...formData.description_point];
        updatedPoints[index] = { ...updatedPoints[index], description: value };
        setFormData({ ...formData, description_point: updatedPoints });

        // Clear description error for this index
        setDescriptionErrors(prev => prev.filter(err => err.index !== index));
    };

    const handleAddDescriptionPoint = () => {
        setFormData({
            ...formData,
            description_point: [...formData.description_point, { description: '' }],
        });
    };

    const handleRemoveDescriptionPoint = (index) => {
        if (formData.description_point.length <= 1) {
            // Don't remove the last one, just clear it
            const updatedPoints = [...formData.description_point];
            updatedPoints[index] = { description: '' };
            setFormData({ ...formData, description_point: updatedPoints });
            return;
        }
        setFormData({
            ...formData,
            description_point: formData.description_point.filter((_, i) => i !== index),
        });
        // Clear description errors for removed index
        setDescriptionErrors(prev => prev.filter(err => err.index !== index));
    };

    // Filter out empty descriptions before submission (documents handled separately now)
    const filterEmptyFields = (data) => {
        const filteredDescriptions = data.description_point.filter(point =>
            point.description && point.description.trim() !== ''
        );

        return {
            ...data,
            description_point: filteredDescriptions
        };
    };

    // Filter out documents that don't have both name and file
    const filterEmptyDocuments = (docs) => {
        return docs.filter(doc =>
            doc.document_name && doc.document_name.trim() !== '' &&
            doc.document_file !== null
        );
    };

    console.log("documentFormData", documentFormData)

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Uploading: please wait...");

        // Pre-calculate steps so progress is a single continuous 0->100 across the whole flow
        const validDocs = filterEmptyDocuments(documentFormData);
        const totalSteps = 1 + validDocs.length + (validDocs.length > 0 ? 1 : 0); // update + files + bulk submit
        const stepWeight = 100 / totalSteps;
        let completedSteps = 0;

        const updateOverallProgress = (localPercent) => {
            const overall = completedSteps * stepWeight + (localPercent / 100) * stepWeight;
            setUploadProgress(Math.min(100, Math.round(overall)));
        };

        try {

            const res = await axiosInstance2.put(
                `/siteproject/prolongationdeviation/${i.id}/`,
                formData,
                {
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                        updateOverallProgress(percentCompleted);
                    },
                }
            );

            if (res.status === 200 || res.status === 201) {
                completedSteps += 1; // main record updated
                updateOverallProgress(0);

                const getId = res.data.id;
                const formDataToSend = new FormData();

                for (let [index, doc] of validDocs.entries()) {
                    formDataToSend.append(`documents[${index}].document_name`, doc.document_name);

                    let fileIndex = 0;

                    if (doc.document_file) {
                        const fileId = await uploadFileInChunks(
                            doc.document_file,
                            doc.document_name,
                            (localPercent) => updateOverallProgress(localPercent)
                        );

                        if (!fileId) {
                            console.error(`❌ Failed to upload file: ${doc.document_name}`);
                            completedSteps += 1;
                            updateOverallProgress(0);
                            continue;
                        }

                        formDataToSend.append("file_ids", fileId);
                        formDataToSend.append(`documents[${fileIndex}][document_name]`, doc.document_name);
                        formDataToSend.append(`documents[${fileIndex}][original_filename]`, doc.document_file.name);

                        fileIndex++;
                        completedSteps += 1; // this file's chunked upload done
                        updateOverallProgress(0);
                    }

                    formDataToSend.append(`documents[${index}].prolongationdeviation`, getId);

                    if (doc.document_file) {
                        formDataToSend.append(`documents[${index}].document_file`, doc.document_file);
                    }
                }
                if (formDataToSend.has("file_ids")) {
                    try {
                        if (formDataToSend.has("file_ids")) {
                            const response = await axiosInstance2.post(
                                `/siteproject/documentbulk/`,
                                formDataToSend,
                                {
                                    onUploadProgress: (progressEvent) => {
                                        const percentCompleted = Math.round(
                                            (progressEvent.loaded * 100) / progressEvent.total
                                        );
                                        updateOverallProgress(percentCompleted);
                                    },
                                    headers: { "Content-Type": "multipart/form-data" },
                                }
                            );

                            if (response.status === 200) {
                                setUploadProgress(100);
                                await getData();
                                handleClose();
                                setTimeout(() => {
                                    toast.dismiss(loadingToastId);
                                    toast.success("Additional Comment updated successfully.", {
                                        autoClose: 1000,
                                        pauseOnHover: true,
                                        closeOnClick: true,
                                    });
                                }, 1000);
                            }
                        }
                        else {
                            if (!formDataToSend.has("file_ids")) {
                                setUploadProgress(100);
                            }
                            await getData();
                            handleClose();
                            setTimeout(() => {
                                toast.dismiss(loadingToastId);
                                toast.success("Additional Comment updated successfully.", {
                                    autoClose: 1000,
                                    pauseOnHover: true,
                                    closeOnClick: true,
                                });
                            }, 1000);
                        }
                    } catch (error) {
                        // handleErrorToast(error);
                    } finally {
                        setLoading(false);
                        toast.dismiss(loadingToastId);
                    }
                } else {

                    await getData();
                    handleClose();
                    setTimeout(() => {
                        toast.dismiss(loadingToastId);
                        toast.success("Additional Comment updated successfully.", {
                            autoClose: 1000,
                            pauseOnHover: true,
                            closeOnClick: true,
                        });
                    }, 1000);
                }

            } else {
                throw new Error("Unexpected response status: " + res.status);
            }
        } catch (err) {
            setUploadProgress(0);
            toast.dismiss(loadingToastId);
            const errorMessage = err.response?.data?.message || err.message || 'Something went wrong';
            toast.error(`Failed to update comment: ${errorMessage}`, {
                autoClose: 3000,
            });
            console.error('Error:', err);
        } finally {
            toast.dismiss(loadingToastId);

            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);
            setLoading(false);
        }
    };


    // Helper function to get description error
    const getDescriptionError = (index) => {
        return descriptionErrors.find(err => err.index === index);
    };


    console.log("Form Data:", formData);
    console.log("Document Form Data:", documentFormData);

    return (
        <>
            <button title="Update Milestone" className="model-edit-button" style={{ background: "black" }} onClick={handleShow}>
                <UpdatePencil modernButton="true" />
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Update" />
            ) : (
                show === true && (
                    <Modal
                        show={show}
                        onHide={handleClose}
                        dialogClassName="modern-project-modal"
                        size="xl"
                    >
                        <Modal.Header className="modern-modal-header">
                            <div className="header-content">
                                <div >
                                    <LucideMessageCircleCode size={25} />

                                </div>
                                <div>
                                    <Modal.Title>Update Additional Comment</Modal.Title>
                                    <p className="header-subtitle">Fill in the details to update a prolongation or deviation comment for the project</p>
                                </div>
                            </div>
                            <button className="close-btn" onClick={handleClose}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18"></line>
                                    <line x1="6" y1="6" x2="18" y2="18"></line>
                                </svg>
                            </button>
                        </Modal.Header>

                        <Modal.Body className="modern-modal-body">
                            <ToastContainer
                                position="top-center"
                                autoClose={3000}
                                hideProgressBar
                            />
                            <form onSubmit={handleSubmit} className="project-form">
                                {/* Comment Type Section */}
                                <div className="section-title">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                        <path d="M8 10h.01" />
                                        <path d="M12 10h.01" />
                                        <path d="M16 10h.01" />
                                    </svg>
                                    <h4>Comment Information</h4>
                                </div>

                                <div className="form-grid">
                                    <div className="form-field">
                                        <label>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                            Comment Type <span className="required">*</span>
                                        </label>
                                        <Select
                                            options={commentTypeOptions}
                                            onChange={(option) => setCommentTypeName(option?.name || null)}
                                            value={commentTypeOptions.find(opt => opt.name === commentTypeName)}
                                            placeholder="Select comment type..."
                                            styles={CSSForSelectModern}
                                            isSearchable
                                            isClearable
                                        />
                                        {errors.commentTypeName && (
                                            <div className="error-message">{errors.commentTypeName}</div>
                                        )}
                                    </div>

                                    <div className="form-field">
                                        <label>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="10" />
                                                <path d="M12 6v6l4 2" />
                                            </svg>
                                            Amount
                                        </label>
                                        <input
                                            type="number"
                                            name="amount"
                                            placeholder="Enter amount if applicable"
                                            value={formData.amount || ''}
                                            onChange={handleInputChange}
                                            className={inputState.amount ? "success" : ""}
                                        />
                                    </div>

                                    <div className="form-field">
                                        <label>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M12 20h9" />
                                                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                                            </svg>
                                            Remark
                                        </label>
                                        <textarea
                                            name="remark"
                                            placeholder="Any additional remarks..."
                                            value={formData.remark || ''}
                                            onChange={handleInputChange}
                                            rows={2}
                                            className={inputState.remark ? "success" : ""}
                                        />
                                    </div>
                                </div>

                                {/* Description Points Section - Moved before Documents as it's required */}
                                <div className="section-title" style={{ marginTop: "24px" }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M4 4h16v16H4z" />
                                        <line x1="8" y1="8" x2="16" y2="8" />
                                        <line x1="8" y1="12" x2="16" y2="12" />
                                        <line x1="8" y1="16" x2="12" y2="16" />
                                    </svg>
                                    <h4>Description Points</h4>
                                    <span style={{ fontSize: "12px", color: "#ef4444", marginLeft: "auto", fontWeight: "normal" }}>
                                        <span className="required">*</span> At least one required
                                    </span>
                                </div>

                                {formData.description_point.map((point, index) => {
                                    const error = getDescriptionError(index);
                                    const hasError = error || (index === 0 && descriptionErrors.some(e => e.index === 0 && e.message.includes('required')));

                                    return (
                                        <div key={`desc-${index}`} className="form-grid" style={{
                                            borderBottom: "1px solid #f1f5f9",
                                            paddingBottom: "16px",
                                            marginBottom: "16px",
                                            borderLeft: hasError ? "3px solid #ef4444" : "none",
                                            paddingLeft: hasError ? "12px" : "0"
                                        }}>
                                            <div className="form-field" style={{ gridColumn: "span 2" }}>
                                                <label>
                                                    Point {index + 1}
                                                    <span className="required">*</span>
                                                </label>
                                                <textarea
                                                    value={point.description || ''}
                                                    onChange={(e) => handleDescriptionChange(index, e.target.value)}
                                                    placeholder="Enter description point..."
                                                    rows={2}
                                                    className={error ? "error" : (point.description?.trim() ? "success" : "")}
                                                />
                                                {error && (
                                                    <div className="error-message">{error.message}</div>
                                                )}
                                                {index === 0 && !error && descriptionErrors.some(e => e.index === 0 && e.message.includes('required')) && (
                                                    <div className="error-message">Description is required!</div>
                                                )}
                                            </div>
                                            <div></div>
                                            <div className="form-field" style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }}>
                                                <button
                                                    type="button"
                                                    className="btn-secondaryy"
                                                    onClick={() => handleRemoveDescriptionPoint(index)}
                                                    style={{ padding: "8px 16px" }}
                                                >
                                                    {formData.description_point.length <= 1 ? 'Clear' : 'Remove'}
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}

                                <button
                                    type="button"
                                    className="btn-secondaryy"
                                    onClick={handleAddDescriptionPoint}
                                    style={{ marginTop: "8px" }}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="12" y1="5" x2="12" y2="19" />
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                    Add Description Point
                                </button>

                                {/* Documents Section */}
                                <div className="section-title" style={{ marginTop: "24px" }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                        <line x1="16" y1="13" x2="8" y2="13" />
                                        <line x1="16" y1="17" x2="8" y2="17" />
                                        <polyline points="10 9 9 9 8 9" />
                                    </svg>
                                    <h4>Documents</h4>
                                    <span style={{ fontSize: "12px", color: "#64748b", marginLeft: "auto", fontWeight: "normal" }}>Optional</span>
                                </div>
                                {documentFormData.map((doc, index) => {
                                    const nameError = getDocumentError(index, 'document_name');
                                    const fileError = getDocumentError(index, 'document_file');
                                    const hasError = nameError || fileError;

                                    return (
                                        <div key={`doc-${index}`} className="form-grid" style={{
                                            borderBottom: "1px solid #f1f5f9",
                                            paddingBottom: "16px",
                                            marginBottom: "16px",
                                            borderLeft: hasError ? "3px solid #ef4444" : "none",
                                            paddingLeft: hasError ? "12px" : "0"
                                        }}>
                                            <div className="form-field">
                                                <label>Document Name</label>
                                                <input
                                                    type="text"
                                                    value={doc.document_name || ''}
                                                    onChange={(e) => handleDocumentChange(index, 'document_name', e.target.value)}
                                                    placeholder="e.g., Approval Letter"
                                                    className={nameError ? "error" : (doc.document_name?.trim() ? "success" : "")}
                                                />
                                                {nameError && (
                                                    <div className="error-message">{nameError.message}</div>
                                                )}
                                            </div>

                                            <div className="form-field">
                                                <label>Upload File</label>
                                                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                                    {!doc.document_file && (
                                                        <div style={{ flex: 1 }}>
                                                            <label htmlFor={`document_file_${index}`} className="svg-icon" style={{ cursor: "pointer", height: "35px" }}>
                                                                <div style={{ height: "20px" }} className={`svg-field form-input align-center ${fileError ? "error" : (doc.document_file ? "success" : "")}`}>
                                                                    <Attachment />

                                                                </div>
                                                            </label>
                                                            <input
                                                                type="file"
                                                                id={`document_file_${index}`}
                                                                accept=".zip, .pdf, .xls, .xlsx, .gif"
                                                                style={{ display: "none" }}
                                                                onChange={(e) => handleDocumentChange(index, 'document_file', e.target.files[0])}
                                                                ref={el => fileInputRefs.current[index] = el}
                                                            />
                                                        </div>
                                                    )}
                                                    {doc.document_file && (
                                                        <button type='button' className="explorer-btn">
                                                            <div className="icon-wrapper">
                                                                <div className="folder-part folder-base"></div>
                                                                <div className="folder-part folder-tab"></div>
                                                                <div className="folder-part folder-accent"></div>
                                                                <div className="folder-part folder-highlight"></div>
                                                            </div>
                                                            <div className="status-indicator"></div>
                                                            <span className="tooltip-label">File Explorer</span>
                                                        </button>
                                                    )}

                                                    {doc.document_file && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleClearDocument(index)}
                                                            className="btn-secondaryy"
                                                            style={{
                                                                padding: "4px 12px",
                                                                fontSize: "12px",
                                                                whiteSpace: "nowrap",
                                                                height: "38px"
                                                            }}
                                                            title="Clear selected file"
                                                        >
                                                            ✕ Clear
                                                        </button>
                                                    )}
                                                </div>

                                                {doc.document_file && (
                                                    <span style={{ fontSize: "12px", color: "#10b981", marginTop: "4px", display: "block" }}>
                                                        ✓ File selected: {doc.document_file.name}
                                                    </span>
                                                )}

                                                {fileError && (
                                                    <div className="error-message">{fileError.message}</div>
                                                )}

                                                {!doc.document_file && doc.document_name?.trim() && !fileError && (
                                                    <span style={{ fontSize: "12px", color: "#f59e0b", marginTop: "4px", display: "block" }}>
                                                        ⚠ Please select a file
                                                    </span>
                                                )}
                                            </div>
                                            <div></div>
                                            {documentFormData.length > 1 &&
                                                <div className="form-field" style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }}>
                                                    <button
                                                        type="button"
                                                        className="btn-secondaryy"
                                                        onClick={() => handleRemoveDocument(index)}
                                                        style={{ padding: "8px 16px" }}
                                                    >
                                                        {documentFormData.length <= 1 ? 'Clear All' : 'Remove'}
                                                    </button>
                                                </div>
                                            }
                                        </div>
                                    );
                                })}

                                <button
                                    type="button"
                                    className="btn-secondaryy"
                                    onClick={handleAddDocument}
                                    style={{ marginTop: "8px" }}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="12" y1="5" x2="12" y2="19" />
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                    Add Another Document
                                </button>

                                {/* Form Actions */}
                                <div className="form-actions">
                                    <button type="button" className="btn-secondaryy" onClick={handleClose}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn-primaryy" disabled={loading}>
                                        {loading ? (
                                            <>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="spinning">
                                                    <circle cx="12" cy="12" r="10" />
                                                    <path d="M12 6v2M12 12v2" />
                                                    <path d="m16 8-2 2M16 16l-2-2" />
                                                    <path d="M8 16l2-2M8 8l2 2" />
                                                </svg>
                                                <span>Submitting...</span>
                                            </>
                                        ) : (
                                            <>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M20 14.66V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5.34" />
                                                    <polygon points="18 2 22 6 12 16 8 16 8 12 18 2" />
                                                </svg>
                                                <span>Update Comment</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                            <ToastContainer
                                position="top-center"
                                autoClose={1000}
                                hideProgressBar={false}
                                newestOnTop={true}
                                closeOnClick
                                rtl={false}
                                pauseOnFocusLoss
                                draggable
                                pauseOnHover
                            />
                        </Modal.Body>
                    </Modal>
                )
            )}
        </>
    );
};


const DeleteProlongation = ({ i, getData }) => {
    const [show, setShow] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => {
        setShow(true);
    };
    const [loading, setLoading] = useState(false);

    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const handleFormSubmitDelete = async (e) => {
        e.preventDefault();
        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Deleting: please wait...");
        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
            }
            setUploadProgress(progress);
        }, 100);

        try {
            // delete all documents linked to this deviation record first
            if (i.documents && i.documents.length > 0) {
                await Promise.all(
                    i.documents.map((doc) =>
                        axios.delete(`${BASE_URL}/siteproject/documents/${doc.id}/`)
                    )
                );
            }

            let res = await axios.delete(
                `${BASE_URL}/siteproject/prolongationdeviation/${i.id}/`
            );

            if (res.status === 200) {
                await getData();
                setShow(false);
                setTimeout(() => {
                    toast.dismiss(loadingToastId);
                    toast.success("Data deleted successfully.", {
                        autoClose: 1000,
                        pauseOnHover: true,
                        closeOnClick: true,
                    });
                }, 1000);
            } else {
                throw new Error("Unexpected response status: " + res.status);
            }

        } catch (err) {
            clearInterval(interval);
            setUploadProgress(0);
            handleErrorToast(err);
        } finally {
            clearInterval(interval);

            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);

            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <button
                title="Delete Insurance"
                className="model-delete-button"
                onClick={handleShow}
            >
                <DeleteDustbin />
            </button>
            {
                showProgressModal ?
                    <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Delete" /> :
                    show === true ?
                        <Modal
                            show={show}
                            onHide={handleClose}
                            dialogClassName="request-leave width-40vw"
                        >
                            <Modal.Header closeButton>
                                <Modal.Title>Delete Comment</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <div className="parent-div">
                                    Are You Sure You Want to delete Comment Data of
                                    <div className="button-models" style={{ marginTop: "40px", justifyContent: "space-evenly" }}>
                                        <button className="cancelbutton" onClick={handleClose}>Cancel</button>
                                        <DeleteButton onclick={handleFormSubmitDelete} />

                                    </div>
                                </div>
                                <ToastContainer
                                    position="top-center"
                                    autoClose={1000}
                                    hideProgressBar={false}
                                    newestOnTop={true}
                                    closeOnClick
                                    rtl={false}
                                    pauseOnFocusLoss
                                    draggable
                                    pauseOnHover
                                />
                            </Modal.Body>
                        </Modal> : ""
            }
        </>
    );
};

export { AddProlongation, UpdateProlongation, DeleteProlongation };